import type { NavigateFunction } from "react-router-dom";

export type GoogleReauthStatus = "success" | "failed" | "account_mismatch";

type StartGoogleReauthInput<TAction> = {
	apiBaseUrl: string;
	returnTo: string;
	storageKey: string;
	action: TAction;
};

type ConsumeGoogleReauthInput = {
	search: string;
	pathname: string;
	navigate: NavigateFunction;
	storageKey: string;
};

type ConsumeGoogleReauthResult<TAction> = {
	status: GoogleReauthStatus;
	action: TAction | null;
} | null;

export function startGoogleReauth<TAction>(input: StartGoogleReauthInput<TAction>) {
	const { apiBaseUrl, returnTo, storageKey, action } = input;
	try {
		window.sessionStorage.setItem(storageKey, JSON.stringify(action));
	} catch {
		// Ignore storage errors and continue with redirect.
	}
	window.location.href = `${apiBaseUrl}/auth/google/reveal/start?returnTo=${encodeURIComponent(returnTo)}`;
}

export function consumeGoogleReauthFromUrl<TAction>(
	input: ConsumeGoogleReauthInput,
): ConsumeGoogleReauthResult<TAction> {
	const { search, pathname, navigate, storageKey } = input;
	const params = new URLSearchParams(search);
	const rawStatus = params.get("googleReauth");
	if (!rawStatus) return null;

	let status: GoogleReauthStatus = "failed";
	if (rawStatus === "success") status = "success";
	else if (rawStatus === "account_mismatch") status = "account_mismatch";

	let action: TAction | null = null;
	try {
		const raw = window.sessionStorage.getItem(storageKey);
		if (raw) action = JSON.parse(raw) as TAction;
	} catch {
		action = null;
	}
	try {
		window.sessionStorage.removeItem(storageKey);
	} catch {
		// Ignore storage errors.
	}

	params.delete("googleReauth");
	const nextSearch = params.toString();
	navigate(
		{
			pathname,
			search: nextSearch ? `?${nextSearch}` : "",
		},
		{ replace: true },
	);

	return { status, action };
}
